import { DateTime } from "luxon"
import { Stack } from "@mui/system"
import { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faArrowRotateRight } from "@fortawesome/sharp-regular-svg-icons"
import { MenuItem, Tooltip, Typography } from "@mui/material"
import { DropdownExportButtonPurchases } from "../../../components/DropdownExportButtonPurchases"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Receipt, ReceiptDialog } from "../../../../receipt/pages/ReceiptDialog"
import { selectEffectiveCompanyId } from "../../../../context/contextSelectors"
import { selectUserGroupId } from "../../../../login/loginSelectors"
import { fetchPurchasesByPeriod, TableOrder } from "../purchasesApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useAppSelector } from "../../../../store/storeHooks"
import type { MRT_ColumnDef } from "material-react-table"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import {
    type DtDateRange,
    StandardDateRangeFilter,
} from "../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { LoadingIconButton } from "../../../../../shared/components/buttons/LoadingButton"
import {
    TableSummaryWrap,
    TableTotalAmountBox,
    TableNumberOfItemsBox,
} from "../../../../../shared/components/table/TableSummaryBox"

export const OrdersContent = () => {
    const companyId = useAppSelector(selectEffectiveCompanyId)
    const userGroupId = useAppSelector(selectUserGroupId)

    const { l10n } = useAppLocalization()

    const { startDate, endDate } = useInitialDateFilters()
    const [dateRange, setDateRange] = useState<DtDateRange>(() => ({
        startDate: startDate ?? DateTime.now().minus({ days: 14 }),
        endDate: endDate ?? DateTime.now(),
    }))

    const [receipt, setReceipt] = useState<Receipt>()

    function showReceipt(row: TableOrder) {
        setReceipt({
            orderId: row.id,
            orderUid: row.uid,
            orderType: row.orderType,
        })
    }

    const lastUpdatedTime = () => {
        const timeInMS = queryResult.dataUpdatedAt
        const dateTime = DateTime.fromMillis(timeInMS)
        return dateTime.toLocaleString(DateTime.TIME_WITH_SECONDS)
    }

    const queryResult = useApiQuery(fetchPurchasesByPeriod, {
        queryName: "purchases-by-period",
        dependencies: {
            companyId: companyId ?? 0,
            userGroupId: userGroupId ?? 0,
            startDate: dateRange.startDate.toISODate(),
            endDate: dateRange.endDate.toISODate(),
        },
        select: (data) => {
            return data.orders.map((order) => ({
                ...order,
                startDate: data.reportDetails.startDate,
                endDate: data.reportDetails.endDate,
            }))
        },
        enabled: companyId !== undefined && userGroupId !== undefined,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(queryResult)

    const columns = useMemo<MRT_ColumnDef<TableOrder>[]>(
        () => [
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("office-purchase-time"),
                    accessorKey: "created",
                },
                (row) => row.created,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("office-purchase-name"),
                accessorKey: "customer.displayName",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("office-purchase-payment-details"),
                accessorKey: "paymentDetails.method",
                Cell: ({ cell }) => {
                    return (
                        <p style={{ inlineSize: "8rem", overflowWrap: "break-word" }}>
                            {cell.getValue() === undefined
                                ? ""
                                : l10n.getStringForEnum(
                                      "purchases-payment-method",
                                      cell.getValue() as string,
                                      cell.getValue() as string,
                                  )}
                        </p>
                    )
                },
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("office-purchase-amount"),
                },
                (row) => row.amount,
                true,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <ApiQueryTable
                columns={columns}
                enableRowSelection
                initialState={{ density: "spacious" }}
                positionActionsColumn="last"
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="office-purchase-receipt">
                        <MenuItem
                            onClick={() => {
                                showReceipt(row.original)
                                closeMenu()
                            }}
                            key={`purchase-receipt-${row.original.id}`}
                        >
                            Receipt
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <Stack justifyContent="center" sx={{ whiteSpace: "nowrap" }}>
                            <LocalizedStrict id="office-last-updated" vars={{ lastUpdated: lastUpdatedTime() }}>
                                <Typography variant="caption" color="text.secondary">
                                    Last updated: {lastUpdatedTime()}
                                </Typography>
                            </LocalizedStrict>
                        </Stack>
                        <DropdownExportButtonPurchases
                            rowsIsSelected={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                            containsRows={table.getPrePaginationRowModel().rows.length === 0}
                            allRowsWithFilter={table.getPrePaginationRowModel().rows}
                            seletecRows={table.getSelectedRowModel().rows}
                            chosenStartDate={dateRange.startDate.toISODate()}
                            chosenEndDate={dateRange.endDate.toISODate()}
                        />
                        <Tooltip title={l10n.getString("office-refresh")}>
                            <LoadingIconButton
                                onClick={() => queryResult.refetch()}
                                aria-label="refresh"
                                loading={queryResult.isRefetching}
                            >
                                <FontAwesomeIcon size="xs" icon={faArrowRotateRight} style={{ whiteSpace: "nowrap" }} />
                            </LoadingIconButton>
                        </Tooltip>
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <StandardDateRangeFilter value={dateRange} onChange={setDateRange} />
                    </StandardTableFilter>
                )}
                renderBottomToolbarCustomActions={({ table }) => (
                    <TableSummaryWrap>
                        <TableNumberOfItemsBox
                            data={table.getFilteredRowModel().rows}
                            isLoading={queryResult.isLoading}
                            title="Total transactions for period"
                        />
                        <TableTotalAmountBox
                            data={table.getFilteredRowModel().rows}
                            isLoading={queryResult.isLoading}
                            accessor={(row) => row.original.amount}
                        />
                    </TableSummaryWrap>
                )}
            />

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={queryResult.data}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}
        </>
    )
}
